import { addressToString, isDefined, isUndefined, schAloftArea } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';

import {
  CartesianGrid,
  ComposedChart,
  DefaultLegendContent,
  DefaultLegendContentProps,
  Legend,
  Line,
  ReferenceArea,
  Scatter,
} from 'recharts';

import { useAppSelector } from '../../../redux/hooks.ts';
import { GraphDescription } from '../components/GraphDescription.tsx';
import { GraphTitle } from '../components/GraphTitle.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../components/SimpleGraphTooltip.tsx';
import {
  ExponentialFitDataPoint,
  SubjectVacantLandSalesChartData,
  VacantLandSalesChartData,
  VacantLandSalesGraphProps,
} from './types.ts';
import { getVacantLandSalesExponentialFit } from './utils.ts';
import { selectSaleDateById } from '../../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { selectSubjectSiteAreaById } from '../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { selectAddress } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';

type VacantLandCostPerSFVsSiteSizeGraphProps = VacantLandSalesGraphProps & {
  vacantLandSalesExponentialFit: ReturnType<typeof getVacantLandSalesExponentialFit>;
};

const LegendContent = ({ payload, ...props }: DefaultLegendContentProps) => (
  <DefaultLegendContent
    {...props}
    payload={[
      ...payload!,
      {
        value: '25th-75th Percentile',
        type: 'rect',
        color: colors.graphAreaBackgroundOpaque,
      },
    ]}
  />
);

export const VacantLandCostPerSFVsSiteSizeGraph = ({
  disableAnimations = false,
  width,
  height,
  vacantLandSalesChartData,
  vacantLandSalesExponentialFit,
}: VacantLandCostPerSFVsSiteSizeGraphProps) => {
  const subjectSiteArea = useAppSelector((state) => selectSubjectSiteAreaById(state, 'subject'));
  const subjectSaleDate = useAppSelector((state) => selectSaleDateById(state, 'subject'));
  const subjectAddress = useAppSelector((state) => selectAddress(state, 'subject'));

  const { getCostPerSFFromSiteSize, lineData } = vacantLandSalesExponentialFit;

  const data: (
    | SubjectVacantLandSalesChartData
    | VacantLandSalesChartData
    | ExponentialFitDataPoint
  )[] = [...vacantLandSalesChartData, ...lineData];

  if (subjectSiteArea) {
    const subjectDataPoint: SubjectVacantLandSalesChartData = {
      address: addressToString(subjectAddress),
      saleDate: subjectSaleDate,
      siteSize: schAloftArea.parse(subjectSiteArea).getUnitAmount('sqft'),
      subjectCostPerSF: getCostPerSFFromSiteSize(
        schAloftArea.parse(subjectSiteArea).getUnitAmount('sqft'),
      ),
    };

    data.push(subjectDataPoint);
  }

  const costPerSfs = vacantLandSalesChartData.map((d) => d.costPerSF);
  const minCostPerSF = Math.min(...costPerSfs);
  const maxCostPerSF = Math.max(...costPerSfs);

  const p25CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.25;
  const p75CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.75;

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <ComposedChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 42, left: 8, bottom: 8, right: 8 }}
        style={{ background: colors.graphBackground, borderRadius: 8 }}
      >
        <GraphTitle>Vacant Land $ / SF vs Site Size</GraphTitle>
        <GraphDescription>
          Displaying all vacant land sales within {subjectAddress.CountyName} county. Sales below the 5th
          percentile and above the 95th percentile have been omitted.
        </GraphDescription>
        <CartesianGrid strokeDasharray="3 3" />
        <Legend verticalAlign="top" align="right" content={LegendContent} />

        <SimpleGraphTooltip<VacantLandSalesChartData & SubjectVacantLandSalesChartData>
          disableAnimations={disableAnimations}
          titleKey="address"
          rows={[
            {
              label: 'Site Size (SF)',
              key: 'siteSize',
              color: 'graphChart',
              valueFormatter: (v) => `${v} SF`,
            },
            {
              label: 'Cost Per SF',
              key: 'subjectCostPerSF',
              color: 'graphAdverse',
              valueFormatter: (v) => `$${v}`,
              shouldHide: (p) => isUndefined(p.subjectCostPerSF),
            },
            {
              label: 'Cost Per SF',
              key: 'costPerSF',
              color: 'graphChart',
              valueFormatter: (v) => `$${v}`,
              shouldHide: (p) => isDefined(p.subjectCostPerSF),
            },
            {
              label: 'Sale Date',
              key: 'saleDate',
            },
            {
              label: 'Distance To Subject',
              key: 'distanceToSubject',
              valueFormatter: (v) => (v ? `${v} miles` : '-'),
              shouldHide: (p) => isDefined(p.subjectCostPerSF),
            },
          ]}
        />

        <GraphXAxis
          type="number"
          dataKey="siteSize"
          label="Site Size (SF)"
          tickFormatter={(val: number) => val.toLocaleString()}
        />
        <GraphYAxis
          type="number"
          label="$ / SF"
          tickFormatter={(val: number) => `$${val.toLocaleString()}`}
        />

        <Scatter
          name="Vacant Land Sales"
          dataKey="costPerSF"
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
        />
        <Scatter
          name="Subject Property"
          dataKey="subjectCostPerSF"
          shape="star"
          legendType="star"
          fill={colors.graphAdverse}
          isAnimationActive={!disableAnimations}
        />

        <Line
          name="Exponential Fit"
          dataKey="lineY"
          type="monotone"
          stroke={colors.graphChart4}
          dot={false}
          activeDot={false}
          isAnimationActive={!disableAnimations}
        />
        <ReferenceArea y1={p25CostPerSF} y2={p75CostPerSF} fill={colors.graphAreaBackground} />
      </ComposedChart>
    </ResponsiveGraphWrapper>
  );
};
